/**
 * LPO email formats: the registry behind the email icon on the clinic orders
 * page. A CLIPBOARD feature, not a transactional email: the app composes text
 * and copies it; nothing goes through send-email, Resend or the notification
 * subscriptions. Adding a format is one more entry in LPO_EMAIL_FORMATS,
 * never a new screen.
 *
 * Bodies are ALWAYS English regardless of the interface locale: they go to
 * hospital procurement teams (Mediclinic, ZIA), so nothing here reads i18n.
 *
 * Privacy: patients flagged is_private are named by their patient code,
 * never by name, the same rule fn_run_delivery_announcement follows
 * (coalesce(patient_code, 'private patient')).
 */
import { formatProductLabel, isCustomLine } from '../productLabel';
import type { OrderListLine } from '@insolech/sdk';

/** The slice of an order the formats need. Built by the modal from the
 *  page's own list rows plus the privacy flags, so the formats stay pure. */
export interface LpoEmailOrder {
  id: string;
  order_number: string;
  created_at: string;
  patient: {
    full_name: string | null;
    is_private: boolean | null;
    patient_code: string | null;
  } | null;
  lines: Array<
    Pick<OrderListLine, 'line_type' | 'solution_subtype' | 'configuration' | 'product' | 'quantity'> & {
      patient_name?: string | null;
    }
  >;
}

export interface LpoEmailContext {
  /** Podiatrist's first name for the sign-off (from profiles.full_name). */
  podiatristFirstName: string;
}

export interface LpoEmailOutput {
  subject: string;
  text: string;
  html: string;
}

export interface LpoEmailFormat {
  /** Stable key stored in lpo_email_ledger.format_key. */
  key: string;
  /** Row label shown in the modal's format list (English; the modal chrome
   *  may localise it, the generated body never does). */
  label: string;
  build: (orders: LpoEmailOrder[], ctx: LpoEmailContext) => LpoEmailOutput;
}

/** "24 Aug 2026": fixed English format, independent of the browser locale. */
export function formatLpoDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getUTCDate());
  const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    d.getUTCMonth()
  ];
  return `${day} ${month} ${d.getUTCFullYear()}`;
}

/** First token of a full name; empty when nothing usable. */
export function firstNameOf(fullName: string | null | undefined): string {
  return (fullName ?? '').trim().split(/\s+/)[0] ?? '';
}

/** Patient label under the privacy rule: code for private patients, name
 *  otherwise, with the marketplace line-level name as the record-less
 *  fallback (resolvePatientDisplay's rule). */
export function lpoPatientLabel(order: LpoEmailOrder): string {
  const p = order.patient;
  if (p?.is_private) return (p.patient_code ?? '').trim() || 'private patient';
  const name = (p?.full_name ?? '').trim();
  if (name) return name;
  const lineName = order.lines.map((l) => (l.patient_name ?? '').trim()).find((n) => n !== '');
  return lineName ?? (p?.patient_code ?? '').trim() ?? '';
}

/** "1 pair" / "2 pairs" for custom-made lines, "1 item" / "3 items" for
 *  marketplace lines (the Excel's PAIRS vs ITEMS wording). */
export function lpoQuantityLabel(line: LpoEmailOrder['lines'][number]): string {
  const qty = Math.max(1, Number(line.quantity ?? 1));
  const unit = isCustomLine(line.line_type) ? 'pair' : 'item';
  return `${qty} ${unit}${qty === 1 ? '' : 's'}`;
}

/** One line per order line: "1 pair PA12 insole for Jane Doe, ordered 24 Aug 2026". No order
 *  number: podiatrists carry their clinic MRN inside the patient name and our id
 *  only confuses procurement (the subject keeps the order COUNT). */
export function lpoOrderLines(order: LpoEmailOrder): string[] {
  const patient = lpoPatientLabel(order);
  const date = formatLpoDate(order.created_at);
  return order.lines.map((line) => {
    const product = formatProductLabel(line as OrderListLine);
    const who = patient ? ` for ${patient}` : '';
    return `${lpoQuantityLabel(line)} ${product}${who}, ordered ${date}`;
  });
}

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * The LPO submission note. The shape Rafael specified: "Hey Team, please
 * raise an LPO for the following orders placed through the INSOLECH portal",
 * one line per order, signed with the user's first name.
 */
export const LPO_SUBMISSION_FORMAT: LpoEmailFormat = {
  key: 'lpo_submission',
  label: 'LPO email',
  build(orders, ctx) {
    const lines = orders.flatMap(lpoOrderLines);
    const count = lines.length;
    const signoffName = ctx.podiatristFirstName.trim();
    const noun = count === 1 ? 'order' : 'orders';
    const subject = `LPO request: ${count} ${noun} placed through the INSOLECH portal`;
    const intro = `Please raise an LPO for the following ${noun} placed through the INSOLECH portal:`;

    const text = [
      'Hey Team,',
      '',
      intro,
      ...lines.map((l) => `- ${l}`),
      '',
      'Many thanks,',
      signoffName,
    ].join('\n');

    const html = [
      '<div style="font-family:Calibri,Arial,sans-serif;font-size:11pt;color:#111">',
      '<p>Hey Team,</p>',
      `<p>${escapeHtml(intro)}</p>`,
      '<ul>',
      ...lines.map((l) => `<li>${escapeHtml(l)}</li>`),
      '</ul>',
      `<p>Many thanks,<br>${escapeHtml(signoffName)}</p>`,
      '</div>',
    ].join('');

    return { subject, text, html };
  },
};

/** The registry. Exactly one format today; a second is a new entry here. */
export const LPO_EMAIL_FORMATS: ReadonlyArray<LpoEmailFormat> = [LPO_SUBMISSION_FORMAT];

export function getLpoEmailFormat(key: string): LpoEmailFormat | undefined {
  return LPO_EMAIL_FORMATS.find((f) => f.key === key);
}
